import { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import NetworkStatus from "./NetworkStatus";
import api from "../lib/api";
import VenusLogo from "./VenusLogo";
import { toast } from "sonner";
import {
  LayoutDashboard, Pill, Warehouse, Building2, Truck, ShoppingCart,
  ClipboardList, ArrowLeftRight, ScrollText, UserCog, LogOut, Activity, ReceiptText, MessageSquare, Wallet, Tag, ScanLine, Menu, X, CalendarClock, TrendingDown, Shield, FileText, Key
} from "lucide-react";

const NAV = [
  { to: "/admin", label: "Tableau de bord", icon: LayoutDashboard, end: true },
  { to: "/super-admin", label: "Super-admin", icon: Shield, superAdminOnly: true, highlight: true },
  { to: "/commandes-clients", label: "Commandes clients", icon: FileText, highlight: true },
  { to: "/livraisons", label: "Livraisons / Tournées", icon: Truck, highlight: true },
  { to: "/produits", label: "Produits", icon: Pill },
  { to: "/scanner", label: "Scanner", icon: ScanLine },
  { to: "/etiquettes", label: "Étiquettes", icon: Tag },
  { to: "/stock", label: "Mouvements", icon: Warehouse },
  { to: "/transferts", label: "Transferts", icon: ArrowLeftRight },
  { to: "/sites", label: "Sites", icon: Building2 },
  { to: "/fournisseurs", label: "Fournisseurs", icon: Truck, roles: ["admin"] },
  { to: "/commandes", label: "Commandes", icon: ClipboardList, roles: ["admin"] },
  { to: "/ventes", label: "Ventes", icon: ShoppingCart },
  { to: "/caisse", label: "Caisse", icon: Wallet },
  { to: "/paiements", label: "Paiements", icon: ReceiptText },
  { to: "/inventaires", label: "Inventaires", icon: Activity },
  { to: "/lots", label: "Lots & Péremption", icon: CalendarClock },
  { to: "/predictions", label: "Prédiction ruptures", icon: TrendingDown },
  { to: "/audit", label: "Audit", icon: ScrollText, roles: ["admin"] },
  { to: "/utilisateurs", label: "Utilisateurs", icon: UserCog, roles: ["admin"] },
  { to: "/sms", label: "Journal SMS", icon: MessageSquare, roles: ["admin"] },
  { to: "/diagnostics", label: "Diagnostics", icon: Activity, supportOnly: true },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const role = user?.role || "viewer";

  useEffect(() => {
    if (user?.role === "admin") {
      api.get("/super-admin/me").then((r) => setIsSuperAdmin(!!r.data?.is_super_admin)).catch(() => {});
    }
  }, [user]);

  const onLogout = async () => {
    await logout();
    navigate("/portail-admin");
  };

  const closeSidebar = () => setSidebarOpen(false);

  return (
    <div className="min-h-screen flex bg-[var(--bg-soft)]">
      {/* Mobile backdrop */}
      {sidebarOpen && (
        <div
          onClick={closeSidebar}
          className="lg:hidden fixed inset-0 bg-black/40 z-40 backdrop-blur-sm"
          data-testid="sidebar-backdrop"
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed lg:static top-0 left-0 h-screen lg:h-auto z-50 w-72 lg:w-64 shrink-0 border-r border-[var(--border-line)] bg-white flex flex-col transition-transform duration-300 lg:translate-x-0 ${sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}
        data-testid="sidebar"
      >
        <div className="p-5 border-b border-[var(--border-line)] flex items-center gap-2.5">
          <VenusLogo size={40} className="shrink-0 shadow-md rounded-xl" />
          <div className="flex-1 min-w-0">
            <div className="font-heading text-base font-extrabold leading-tight tracking-tight">VÉNUS<span className="text-[var(--primary)]"> MÉDICAL</span></div>
            <div className="label-mono mt-1 truncate" data-testid="org-name">{user?.organization?.name || "Matériels & Consommables Médicaux"}</div>
          </div>
          <button onClick={closeSidebar} className="lg:hidden p-1 text-[var(--text-muted)]" data-testid="close-sidebar"><X size={20}/></button>
        </div>

        <nav className="flex-1 py-3 px-2 overflow-y-auto">
          {NAV.filter(n => {
            if (n.superAdminOnly) return isSuperAdmin;
            if (n.supportOnly) return !!user?.is_support;
            if (n.roles && !n.roles.includes(role)) return false;
            return true;
          }).map(({ to, label, icon: Icon, end, highlight }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              onClick={closeSidebar}
              data-testid={`nav-${to.replace("/", "") || "dashboard"}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 my-0.5 text-sm font-medium transition-all rounded-lg ${
                  highlight && !isActive
                    ? "bg-gradient-to-r from-[var(--primary,#C2569B)]/10 to-transparent text-[var(--primary-dark,#8E3A6D)] font-semibold border border-[var(--primary,#C2569B)]/30"
                    : isActive
                    ? "bg-[var(--primary-soft)] text-[var(--primary-dark)]"
                    : "text-[var(--text-muted)] hover:text-[var(--text)] hover:bg-[var(--bg-soft)]"
                }`
              }
            >
              <Icon size={16} strokeWidth={highlight ? 2.2 : 1.8} />
              {label}
            </NavLink>
          ))}
        </nav>

        <div className="p-4 border-t border-[var(--border-line)]">
          <div className="mb-3">
            <div className="label-mono mb-1">Connecté</div>
            <div className="text-sm font-medium truncate" data-testid="current-user-name">{user?.name}</div>
            <div className="text-xs text-[var(--text-muted)] truncate font-data">{user?.email}</div>
            <span className={`inline-block mt-1.5 ${role === "admin" ? "badge-info" : role === "user" ? "badge-success" : "badge-warn"}`}>
              {role}
            </span>
          </div>
          <button data-testid="change-password-button" onClick={() => setShowPwd(true)} className="btn-ghost w-full flex items-center justify-center gap-2 text-sm mb-2">
            <Key size={14} /> Changer mot de passe
          </button>
          <button data-testid="logout-button" onClick={onLogout} className="btn-ghost w-full flex items-center justify-center gap-2 text-sm">
            <LogOut size={14} /> Déconnexion
          </button>
        </div>
      </aside>

      {showPwd && <ChangePasswordModal onClose={() => setShowPwd(false)} />}

      {/* Main */}
      <main className="flex-1 overflow-x-hidden min-w-0">
        <div className="px-4 sm:px-6 lg:px-8 py-3 lg:py-4 border-b border-[var(--border-line)] bg-white flex items-center justify-between gap-3 sticky top-0 z-30">
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden flex items-center gap-2 text-[var(--text)]"
            data-testid="open-sidebar"
          >
            <Menu size={22} strokeWidth={2.2}/>
            <VenusLogo size={24} className="rounded-md" />
            <span className="font-heading font-bold text-base">VÉNUS<span className="text-[var(--primary)]"> MÉDICAL</span></span>
          </button>
          <div className="hidden lg:block"></div>
          <NetworkStatus />
        </div>
        <div className="px-4 sm:px-6 lg:px-8 py-4 lg:py-6 stagger fade-in">{children}</div>
      </main>
    </div>
  );
}

// ---- Change password modal ----
function ChangePasswordModal({ onClose }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) return toast.error("Au moins 8 caractères");
    if (next !== confirm) return toast.error("Les mots de passe ne correspondent pas");
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Mot de passe changé. Reconnectez-vous prochainement.");
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[70] flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={submit} className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-5" onClick={(e) => e.stopPropagation()} data-testid="change-password-modal">
        <div className="flex items-center justify-between mb-4">
          <div className="font-heading font-extrabold text-lg flex items-center gap-2"><Key size={18}/>Changer mon mot de passe</div>
          <button type="button" onClick={onClose} className="w-9 h-9 rounded-full hover:bg-[var(--bg-soft,#f7f7f7)] flex items-center justify-center" aria-label="Fermer"><X size={20}/></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="label-mono mb-1 block">Mot de passe actuel</label>
            <input type="password" required className="input-base" value={current} onChange={(e) => setCurrent(e.target.value)} data-testid="current-pwd" autoComplete="current-password"/>
          </div>
          <div>
            <label className="label-mono mb-1 block">Nouveau mot de passe</label>
            <input type="password" required minLength={8} className="input-base" value={next} onChange={(e) => setNext(e.target.value)} data-testid="new-pwd" autoComplete="new-password"/>
            <div className="text-xs text-[var(--text-muted)] mt-1">Au moins 8 caractères</div>
          </div>
          <div>
            <label className="label-mono mb-1 block">Confirmer</label>
            <input type="password" required minLength={8} className="input-base" value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="confirm-pwd" autoComplete="new-password"/>
          </div>
        </div>
        <div className="flex gap-2 mt-5">
          <button type="button" onClick={onClose} className="btn-ghost flex-1">Annuler</button>
          <button type="submit" disabled={saving} className="btn-primary flex-1" data-testid="submit-pwd">{saving ? "..." : "Mettre à jour"}</button>
        </div>
      </form>
    </div>
  );
}

